import { Tooltip } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { budgetFormat } from "../../../utils/number/budget-format.ts"
import { WidgetFrame } from "../WidgetFrame"
import { KpiExplanation, emptyStateText } from "../kpi/KpiWidget"
import { getKpiIconEntry } from "../kpi/kpiIconMap"
import { type IconTileVariant, iconTileTints, neutral } from "../tokens"
import type {
  KpiTile,
  SnapshotAxis,
  SnapshotCell,
  SnapshotPayload,
  WidgetProps
} from "../types"

interface RowStyle {
  tint: IconTileVariant
  icon: string
}

/** Income reads as gain, cost as spend, profit as the brand measure. */
const ROW_STYLES: Record<string, RowStyle> = {
  income: { tint: "success", icon: "currency" },
  cost: { tint: "warning", icon: "wallet" },
  profit: { tint: "teal", icon: "trending" }
}

const DEFAULT_ROW_STYLE: RowStyle = { tint: "teal", icon: "activity" }

const InfoIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5" />
    <path d="M12 8h.01" />
  </svg>
)

type Translate = (key: string, defaultValue: string) => string

/**
 * The one-line reading under each figure — deliberately not the tooltip's
 * `description`, which is a paragraph.
 *
 * "Taken in" rather than "invoiced": the to-date column counts lines marked
 * completed, which is not the same event as raising an invoice.
 */
function captionFor(key: string, t: Translate): string {
  switch (key) {
    case "total_expected_income":
      return t(
        "portfolio.financialSnapshot.caption.totalExpectedIncome",
        "Full contract value"
      )
    case "billed_to_date":
      return t(
        "portfolio.financialSnapshot.caption.billedToDate",
        "Taken in so far"
      )
    case "remaining_to_bill":
      return t(
        "portfolio.financialSnapshot.caption.remainingToBill",
        "Left to take in"
      )
    case "budgeted_cost":
      return t(
        "portfolio.financialSnapshot.caption.budgetedCost",
        "Full contract expenses"
      )
    case "cost_to_date":
      return t(
        "portfolio.financialSnapshot.caption.costToDate",
        "Paid out so far"
      )
    case "cost_to_complete":
      return t(
        "portfolio.financialSnapshot.caption.costToComplete",
        "Left to spend"
      )
    case "estimated_gross_profit":
      return t(
        "portfolio.financialSnapshot.caption.estimatedGrossProfit",
        "Expected income minus cost"
      )
    case "gross_profit_to_date":
      return t(
        "portfolio.financialSnapshot.caption.grossProfitToDate",
        "Taken in minus paid out"
      )
    case "remaining_gross_profit":
      return t(
        "portfolio.financialSnapshot.caption.remainingGrossProfit",
        "Profit still to earn"
      )
    default:
      return ""
  }
}

/** The explanation components speak KpiTile; a cell is the same shape minus the layout. */
const asTile = (
  cell: SnapshotCell,
  filtersIgnored?: SnapshotPayload["filters_ignored"]
): KpiTile => ({
  id: cell.key,
  title: cell.title,
  value: cell.value,
  unit: cell.unit,
  drill_to: "",
  has_data: cell.has_data,
  description: cell.description,
  formula: cell.formula,
  basis: cell.basis,
  ...(cell.empty_reason ? { empty_reason: cell.empty_reason } : {}),
  ...(filtersIgnored?.length ? { filters_ignored: filtersIgnored } : {})
})

const formatValue = (cell: SnapshotCell, currency: string): string => {
  const numeric =
    typeof cell.value === "number" ? cell.value : Number(cell.value)
  if (!Number.isFinite(numeric)) return String(cell.value ?? "—")
  return cell.unit === "currency"
    ? budgetFormat(numeric, { currency })
    : String(numeric)
}

/**
 * Nothing planned and nothing spent are both zero, and the client reads a blank
 * value line as a figure the page failed to load. The reason the datum is
 * missing moves to the caption slot rather than standing in for the number.
 */
const withZeroWhenAbsent = (cell: SnapshotCell): SnapshotCell =>
  cell.has_data === false ? { ...cell, value: 0 } : cell

const SnapshotCellBody = ({
  cell,
  currency,
  filtersIgnored
}: {
  cell: SnapshotCell
  currency: string
  filtersIgnored?: SnapshotPayload["filters_ignored"]
}) => {
  const { t } = useTranslation()
  const tile = asTile(cell, filtersIgnored)
  const hasExplanation = Boolean(
    cell.description || cell.formula || filtersIgnored?.length
  )
  const absent = cell.has_data === false
  const caption = captionFor(cell.key, t)
  const emptyText = emptyStateText(tile, t)
  const showEmptyAsValue =
    absent && cell.empty_reason === "nothing_billed"

  return (
    <div
      data-testid={`snapshot-cell-${cell.key}`}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        minWidth: 0,
        padding: "2px 0"
      }}
    >
      <div
        style={{ display: "flex", alignItems: "center", gap: 5, minWidth: 0 }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: neutral.muted,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }}
        >
          {cell.title}
        </span>
        {hasExplanation ? (
          <Tooltip
            title={<KpiExplanation tile={tile} />}
            trigger={["hover", "focus"]}
          >
            <button
              type="button"
              data-testid={`snapshot-info-${cell.key}`}
              aria-label={t(
                "portfolio.tile.infoLabel",
                "How this is calculated"
              )}
              style={{
                border: "none",
                background: "transparent",
                padding: 0,
                lineHeight: 0,
                color: neutral.faint,
                cursor: "help",
                flexShrink: 0
              }}
            >
              <InfoIcon width={12} height={12} />
            </button>
          </Tooltip>
        ) : null}
      </div>

      <span
        data-testid={`snapshot-value-${cell.key}`}
        style={{
          fontSize: 22,
          fontWeight: 700,
          lineHeight: 1.25,
          color: absent ? neutral.muted : neutral.ink,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis"
        }}
      >
        {showEmptyAsValue
          ? emptyText
          : formatValue(withZeroWhenAbsent(cell), currency)}
      </span>

      {absent && !showEmptyAsValue ? (
        <span
          data-testid={`snapshot-empty-${cell.key}`}
          style={{ fontSize: 12, fontStyle: "italic", color: neutral.faint }}
        >
          {emptyText}
        </span>
      ) : caption ? (
        <span
          data-testid={`snapshot-caption-${cell.key}`}
          style={{ fontSize: 12, fontStyle: "italic", color: neutral.faint }}
        >
          {caption}
        </span>
      ) : null}
    </div>
  )
}

const RowRail = ({ row }: { row: SnapshotAxis }) => {
  const style = ROW_STYLES[row.key] ?? DEFAULT_ROW_STYLE
  const tint = iconTileTints[style.tint]
  const { icon: Icon } = getKpiIconEntry({ id: "", icon: style.icon })

  return (
    <div
      data-testid={`snapshot-row-${row.key}`}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        paddingLeft: 12,
        borderLeft: `3px solid ${tint.fg}`,
        minWidth: 0
      }}
    >
      <span
        aria-hidden
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 30,
          height: 30,
          borderRadius: 8,
          background: tint.bg,
          color: tint.fg,
          flexShrink: 0
        }}
      >
        <Icon width={16} height={16} />
      </span>
      <span
        style={{
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          color: neutral.ink
        }}
      >
        {row.title}
      </span>
    </div>
  )
}

const SnapshotGrid = ({
  snapshot,
  currency
}: {
  snapshot: SnapshotPayload
  currency: string
}) => {
  const byPosition = new Map<string, SnapshotCell>()
  for (const cell of snapshot.cells)
    byPosition.set(`${cell.row}.${cell.column}`, cell)

  const columns: SnapshotAxis[] = snapshot.columns ?? []
  const rows: SnapshotAxis[] = snapshot.rows ?? []

  const dividerAbove = (index: number): React.CSSProperties =>
    index === 0 ? {} : { borderTop: `1px solid ${neutral.border}` }

  return (
    <div style={{ overflowX: "auto" }}>
      <div
        data-testid="snapshot-grid"
        style={{
          display: "grid",
          // Capped rather than 1fr: stretched columns put hundreds of pixels
          // between a figure and the next one on a wide monitor, which is the
          // opposite of reading a row across.
          gridTemplateColumns: `minmax(120px, 152px) repeat(${columns.length}, minmax(180px, 264px))`,
          justifyContent: "start",
          columnGap: 24,
          rowGap: 0,
          minWidth: 120 + columns.length * 204
        }}
      >
        <span />
        {columns.map((column) => (
          <span
            key={column.key}
            data-testid={`snapshot-column-${column.key}`}
            style={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: neutral.muted,
              paddingBottom: 10
            }}
          >
            {column.title}
          </span>
        ))}

        {rows.map((row, index) => (
          <React.Fragment key={row.key}>
            <div style={{ ...dividerAbove(index), padding: "16px 0" }}>
              <RowRail row={row} />
            </div>
            {columns.map((column) => {
              const cell = byPosition.get(`${row.key}.${column.key}`)
              return (
                <div
                  key={column.key}
                  style={{ ...dividerAbove(index), padding: "16px 0" }}
                >
                  {cell ? (
                    <SnapshotCellBody
                      cell={cell}
                      currency={currency}
                      filtersIgnored={snapshot.filters_ignored}
                    />
                  ) : null}
                </div>
              )
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  )
}

export const SnapshotWidget = ({
  widget,
  payload,
  state,
  currency = "USD",
  onRefresh,
  onExport
}: WidgetProps) => {
  const snapshot = payload?.snapshot
  const hasCells = Boolean(snapshot?.cells?.length)

  return (
    <WidgetFrame
      title={widget.title}
      state={state === "ready" && !hasCells ? "empty" : state}
      capabilities={widget.capabilities}
      widgetId={widget.id}
      payload={payload}
      onRefresh={onRefresh}
      onExport={onExport}
    >
      {snapshot ? (
        <SnapshotGrid
          snapshot={snapshot}
          currency={payload?.currency ?? currency}
        />
      ) : null}
    </WidgetFrame>
  )
}
